import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { formatCPF, formatPhone, unmask } from '../utils/formatters';
import FormInput from './common/FormInput';
import FormContainer from './common/FormContainer';
import FormRow from './common/FormRow';
import BottomSheet from './common/BottomSheet';

const Button = styled.button`
  padding: 1rem;
  background-color: ${props => props.theme.primary || '#e74c3c'};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;
  box-shadow: 0 4px 6px ${props => props.theme.primary ? `${props.theme.primary}33` : 'rgba(231, 76, 60, 0.2)'};

  &:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
    box-shadow: 0 6px 12px ${props => props.theme.primary ? `${props.theme.primary}4D` : 'rgba(231, 76, 60, 0.3)'};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: ${props => props.theme.primary ? `${props.theme.primary}80` : '#feb2b2'};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SwitchModeContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const SwitchModeButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary || '#e74c3c'};
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: color 0.2s;

  &:hover {
    filter: brightness(0.9);
    text-decoration: underline;
  }
`;

const EyeButton = styled.button`
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  transition: color 0.2s;

  &:hover {
    color: #4a5568;
  }

  i {
    font-size: 1.1rem;
  }
`;

interface LoginModalProps {
  onSuccess?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ onSuccess }) => {
  const { login, register, showLoginModal, setShowLoginModal, setShowForgotPasswordModal, setShowFirstAccessModal } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  
  // Login fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Register fields
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState<'MALE' | 'FEMALE'>('MALE');
  const [birthDate, setBirthDate] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (!showLoginModal) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleFormSubmit();
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);
    
    try {
      if (isLoginMode) {
        if (!email || !password) {
          toast.error('Por favor, preencha todos os campos');
          setIsLoading(false);
          return;
        }
        
        const success = await login({
          cpfOrPhone: email,
          password: password
        });
        
        if (success) {
          toast.success('Bem-vindo de volta!');
          setShowLoginModal(false);
          if (onSuccess) onSuccess();
        } else {
          toast.error('Celular/CPF ou senha inválidos');
        }
      } else {
        // Register mode
        if (!name || !cpf || !email || !password || !phone || !birthDate) {
          toast.error('Por favor, preencha todos os campos obrigatórios');
          setIsLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          toast.error('As senhas não coincidem');
          setIsLoading(false);
          return;
        }
        
        const success = await register({
          name,
          cpf: unmask(cpf),
          email,
          password,
          phone: unmask(phone),
          gender,
          birthDate
        });

        if (success) {
          toast.success('Cadastro realizado com sucesso!');
          setShowLoginModal(false);
          if (onSuccess) onSuccess();
        } else {
          // Cadastro OK, mas o auto-login não completou: volta para o login.
          toast.success('Cadastro realizado! Entre com seu CPF/celular e senha.');
          setIsLoginMode(true);
        }
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.message;
      toast.error(backendMessage || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowLoginModal(false);
  };

  const openForgotPassword = () => {
    setShowLoginModal(false);
    setShowForgotPasswordModal(true);
  };

  const goToFirstAccess = () => {
    setShowLoginModal(false);
    setShowFirstAccessModal(true);
  };

  return (
    <BottomSheet
      isOpen={showLoginModal}
      onClose={handleClose}
      maxWidth="450px"
      zIndex={10500}
      title={isLoginMode ? 'Entrar' : 'Criar Conta'}
    >
        <div style={{ padding: '1.5rem 2rem 2rem' }}>
          <FormContainer as="form" onSubmit={handleSubmit}>
            {!isLoginMode && (
              <>
                <FormInput
                  label="Nome completo"
                  type="text"
                  placeholder="Seu nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <FormRow>
                  <FormInput
                    label="CPF"
                    type="text"
                    placeholder="000.000.000-00"
                    value={cpf}
                    onChange={(e) => setCpf(formatCPF(e.target.value))}
                    disabled={isLoading}
                    maxLength={14}
                    required
                  />
                  <FormInput
                    label="Telefone"
                    type="tel"
                    placeholder="(00) 00000-0000"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    disabled={isLoading}
                    maxLength={15}
                    required
                  />
                </FormRow>
                <FormRow>
                  <FormInput
                    label="Gênero"
                    as="select"
                    value={gender}
                    onChange={(e) => setGender(e.target.value as 'MALE' | 'FEMALE')}
                    disabled={isLoading}
                    required
                  >
                    <option value="MALE">Masculino</option>
                    <option value="FEMALE">Feminino</option>
                  </FormInput>
                  <FormInput
                    label="Data de Nascimento"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </FormRow>
              </>
            )}
            
            <FormInput
              label={isLoginMode ? "Celular ou CPF" : "E-mail"}
              type={isLoginMode ? "text" : "email"}
              placeholder={isLoginMode ? "Digite seu celular ou CPF" : "seu@email.com"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
            />
            
            <FormInput
              label="Senha"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              rightElement={
                <EyeButton type="button" onClick={() => setShowPassword(!showPassword)}>
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </EyeButton>
              }
            />

            {!isLoginMode && (
              <FormInput
                label="Confirmar Senha"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                required
                rightElement={
                  <EyeButton type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </EyeButton>
                }
              />
            )}
            
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (isLoginMode ? 'Entrando...' : 'Cadastrando...') : (isLoginMode ? 'Entrar' : 'Cadastrar')}
            </Button>
          </FormContainer>

          {isLoginMode && (
            <SwitchModeContainer style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <SwitchModeButton type="button" onClick={goToFirstAccess}>
                Primeiro acesso? Criar senha
              </SwitchModeButton>
              <SwitchModeButton type="button" onClick={openForgotPassword}>
                Esqueci minha senha
              </SwitchModeButton>
            </SwitchModeContainer>
          )}

          <SwitchModeContainer>
            <SwitchModeButton onClick={() => {
              setIsLoginMode(!isLoginMode);
            }}>
              {isLoginMode ? 'Não tem uma conta? Cadastre-se' : 'Já tem uma conta? Faça login'}
            </SwitchModeButton>
          </SwitchModeContainer>
        </div>
    </BottomSheet>
  );
};

export default LoginModal;